import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Circle, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useStore, money, waLink } from "@/context/StoreContext";

const FLOW = ["Return Requested", "Under Review", "Approved", "Pickup Scheduled", "Item Picked Up", "Item Received", "Quality Check", "Resolution", "Completed"];
const STATUS_INDEX = { requested: 0, under_review: 1, approved: 2, pickup_scheduled: 3, picked_up: 4, item_received: 5, quality_check: 6, processing: 7, completed: 8, rejected: -1 };

export default function ReturnDetail() {
  const { id } = useParams();
  const { settings } = useStore();
  const [r, setR] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/returns/${id}`).then((res) => setR(res.data)).catch(() => {}); }, [id]);
  if (!r) return <div className="py-24 text-center text-sharoa-muted">Loading…</div>;
  const idx = STATUS_INDEX[r.status] ?? 0;
  const rejected = r.status === "rejected";

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-12">
      <p className="text-xs uppercase tracking-widest text-sharoa-muted">Return</p>
      <h1 className="font-serif text-4xl">#{r.return_number}</h1>
      <div className="grid grid-cols-2 gap-3 text-sm mt-5 bg-sharoa-ivory p-5">
        <div><p className="text-xs text-sharoa-muted">Order</p><p className="font-mono">#{r.order_number}</p></div>
        <div><p className="text-xs text-sharoa-muted">Requested</p><p>{new Date(r.created_at).toLocaleDateString("en-IN")}</p></div>
        <div><p className="text-xs text-sharoa-muted">Deadline</p><p>{r.return_deadline}</p></div>
        <div><p className="text-xs text-sharoa-muted">Resolution</p><p className="capitalize">{r.resolution?.replace("_", " ")}</p></div>
      </div>

      <div className="mt-6 divide-y divide-sharoa-line border-y border-sharoa-line">
        {r.items?.map((it) => (
          <div key={it.sku} className="flex gap-3 py-3">
            <img src={it.image} alt="" className="w-14 h-18 object-cover bg-sharoa-ivory" />
            <div><p className="font-serif text-sm">{it.name}</p><p className="text-xs text-sharoa-muted">{it.size}·{it.color}·Qty {it.return_quantity} · {money(it.price)}</p></div>
          </div>
        ))}
      </div>
      <p className="text-sm text-sharoa-muted mt-3">Reason: {r.reason}{r.comments ? ` · ${r.comments}` : ""}</p>

      {rejected ? (
        <div className="mt-6 bg-sharoa-pale p-5">
          <p className="font-serif text-lg text-sharoa-charcoal">Return Rejected</p>
          <p className="text-sm text-sharoa-muted mt-1">{r.reject_reason || "This return could not be approved."}</p>
        </div>
      ) : (
        <div className="mt-6">
          <h2 className="font-serif text-xl mb-3">Status</h2>
          {FLOW.map((s, i) => {
            const done = i <= idx, current = i === idx;
            return (
              <div key={s} className="flex items-center gap-3 py-1.5">
                {done ? <CheckCircle2 size={19} className={current ? "text-sharoa-rose" : "text-sharoa-mauve"} /> : <Circle size={19} className="text-sharoa-line" />}
                <span className={`text-sm ${current ? "font-semibold" : done ? "text-sharoa-charcoal" : "text-sharoa-muted"}`}>{s}</span>
              </div>
            );
          })}
        </div>
      )}

      {r.refund && (
        <div className="mt-6 bg-sharoa-ivory p-5 text-sm">
          <p className="font-serif text-lg">Refund Processed</p>
          <p className="text-sharoa-muted mt-1">{money(r.refund.amount)} · {r.refund.method} · Ref {r.refund.reference || "—"}</p>
        </div>
      )}

      <div className="flex gap-3 mt-7">
        <Link to="/contact" className="flex-1 border border-sharoa-charcoal text-xs uppercase tracking-widest py-3.5 text-center">Contact SHAROA</Link>
        <a href={waLink(settings?.social?.whatsapp?.number, `Hi SHAROA ♡ I need help with return #${r.return_number}.`)} target="_blank" rel="noreferrer"
          className="flex-1 flex items-center justify-center gap-2 border border-[#25D366] text-[#128C4A] text-xs uppercase tracking-widest py-3.5"><MessageCircle size={15} /> Chat on WhatsApp</a>
      </div>
    </div>
  );
}
